/**
 * The dialog's live pairing state: polls the PairingSource (pairing.ts, the seam) while the
 * dialog is open and folds what it lists, and the machines in the store, into one phase.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { PairRequestView } from '@orgus/protocol';
import { ApiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { mockPairing, pairingPhase, pairingSource } from './pairing.ts';
import type { AddMachinePreset, ConsoleReach, PairingPhase } from './pairing.ts';

const POLL_MS = 2000;

export interface PairingState {
  phase: PairingPhase;
  /** Approve or deny is in flight. */
  busy: 'approve' | 'deny' | null;
  /** The last approve or deny failed: shown inline, with the request still there to try again. */
  error: unknown;
  approve(name?: string): Promise<void>;
  deny(): Promise<void>;
  /** Back to waiting after a declined or expired request. */
  restart(): void;
}

export function usePairing(open: boolean, preset?: AddMachinePreset, reach: ConsoleReach = 'loopback'): PairingState {
  const [phase, setPhase] = useState<PairingPhase>({ kind: 'waiting' });
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const requests = useRef<PairRequestView[]>([]);
  const ctx = useRef<{ known: Set<string>; openedAt: number }>({ known: new Set(), openedAt: 0 });
  const machines = useOrgus((s) => s.machines);

  // Open: remember which machines already exist, then poll for requests until it closes.
  useEffect(() => {
    if (!open) return;
    ctx.current = { known: new Set(Object.keys(useOrgus.getState().machines)), openedAt: Date.now() };
    requests.current = [];
    setPhase({ kind: 'waiting' });
    setBusy(null);
    setError(null);
    if (useOrgus.getState().mode === 'mock') mockPairing.reset(preset, reach);
    const source = pairingSource();
    const ctrl = new AbortController();
    let timer: ReturnType<typeof setTimeout> | null = null;
    // A remote console is never shown a request before approval: it only watches for the machine.
    // A server without pairing (404) is not an error: it may still gain a machine by the developer path.
    let supported = reach === 'loopback';
    const tick = async () => {
      if (supported) {
        try {
          requests.current = await source.pending(ctrl.signal);
        } catch (e) {
          if (ctrl.signal.aborted) return;
          if (e instanceof ApiError && e.status === 404) supported = false;
        }
      }
      if (ctrl.signal.aborted) return;
      setPhase((p) => pairingPhase(p, { requests: requests.current, machines: Object.values(useOrgus.getState().machines), ...ctx.current, now: Date.now() }));
      timer = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
    return () => {
      ctrl.abort();
      if (timer) clearTimeout(timer);
    };
  }, [open, preset, reach]);

  // A machine that connects shows at once, without waiting for the next poll.
  useEffect(() => {
    if (!open) return;
    setPhase((p) => pairingPhase(p, { requests: requests.current, machines: Object.values(machines), ...ctx.current, now: Date.now() }));
  }, [open, machines]);

  const decide = useCallback(
    async (kind: 'approve' | 'deny', name?: string) => {
      if (phase.kind !== 'approve') return;
      const id = phase.request.requestId;
      setBusy(kind);
      setError(null);
      try {
        const source = pairingSource();
        const r = kind === 'approve' ? await source.approve(id, { ...(name ? { name } : {}), autoUpdate: true }) : await source.deny(id);
        requests.current = requests.current.map((x) => (x.requestId === id ? r : x));
        setPhase(r.state === 'approved' ? { kind: 'joining', request: r } : { kind: 'ended', request: r });
      } catch (e) {
        setError(e);
      } finally {
        setBusy(null);
      }
    },
    [phase],
  );

  return {
    phase,
    busy,
    error,
    approve: (name) => decide('approve', name),
    deny: () => decide('deny'),
    restart: () => {
      ctx.current = { ...ctx.current, openedAt: Date.now() };
      setError(null);
      setPhase({ kind: 'waiting' });
    },
  };
}
